import {
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardMedia,
    Container,
    Theme,
    Typography,
} from '@mui/material';
import {Profile} from '../../types/api';
import SocialCard from './SocialCard';
import Links from './Links';
import Bio from './Bio';
import LoadingImg from '../Ui/LoadingImg';
import {icons} from './icons';
import {useTranslation} from 'react-i18next';
import React, {memo, useState} from 'react';
import LogoWrapper from './LogoWrapper';
import {isIOS} from 'react-device-detect';
import {coverHeight, coverWidth} from '../../lib/style';
import defaultCover from '../../assets/images/default-cover-2x.jpg';
import {getBestTranslatedValue} from '../../lib/localeHelper';
import {humanizeUrl} from '../../lib/utils';
import PoweredBy from '../Ui/PoweredBy.tsx';
import RichBlocks from './RichBlocks.tsx';
import {addSourceTracker, TrackingSource} from '../../lib/tracking.ts';
import {createShareUrl} from '../../lib/urlHelper.ts';
import {isInMobileApp, notifyMobileApp} from '../../lib/mobileAppBridge.ts';
import {PreviewProfileProps} from './types.ts';

type Props = {
    profile: Profile;
    showExchangeModal: () => void;
} & PreviewProfileProps;

function ProfileViewContent({
    profile,
    isPreview = false,
    showExchangeModal,
    userLocales,
}: Props) {
    const {t} = useTranslation('app');

    const sizes = {
        xs: 80,
        md: 100,
        lg: 120,
    };

    const [linkCopied, setLinkCopied] = useState(false);

    const {
        kompany,
        title: titleFallback,
        translations,
        vcfUrl,
        hideCompanyName,
        options,
        firstName: firstNameFallback,
        lastName: lastNameFallback,
        bio: bioFallback,
        locale,
        cover: profileCover,
    } = profile;

    const cover = profileCover ?? kompany?.cover;

    const title = getBestTranslatedValue(
        translations ?? {},
        'title',
        titleFallback,
        locale,
        userLocales,
    );
    const firstName = getBestTranslatedValue(
        translations ?? {},
        'firstName',
        firstNameFallback,
        locale,
        userLocales,
    );
    const lastName = getBestTranslatedValue(
        translations ?? {},
        'lastName',
        lastNameFallback,
        locale,
        userLocales,
    );
    const profileBio = getBestTranslatedValue(
        translations ?? {},
        'bio',
        bioFallback,
        locale,
        userLocales,
    );

    const kompanyBio = kompany
        ? getBestTranslatedValue(
              kompany.translations ?? {},
              'bio',
              kompany.bio,
              kompany.locale,
              userLocales,
          )
        : undefined;

    const bio = profileBio || kompanyBio;

    const kompanyName = kompany
        ? getBestTranslatedValue(
              kompany.translations ?? {},
              'displayName',
              kompany.displayName,
              kompany.locale,
              userLocales,
          )
        : undefined;

    const fullName =
        (firstName
            ? `${firstName}${lastName ? ` ${lastName}` : ''}`
            : kompanyName) || '';

    const socials = [
        ...((kompany && kompany.socials) || []),
        ...(profile.socials || []),
    ];

    const links = [
        ...(kompany?.links || []).map(l => ({
            ...l,
            locale: kompany?.locale,
        })),
        ...(profile.links || []),
    ];

    const blocks = [
        ...(kompany?.blocks || []).map(b => ({
            ...b,
            locale: kompany?.locale,
        })),
        ...(profile.blocks || []).map(b => ({
            ...b,
            locale: profile.locale,
        })),
    ];

    // The share URL, not the address bar: shared links are tracked apart from direct views.
    const share = (): void => {
        const title = `${profile.profileLabel} - VKARD.io`;
        const url = createShareUrl(profile, TrackingSource.Share, true);

        // The app opens the phone's share sheet: an Android WebView has none, and iOS
        // then behaves the same.
        if (isInMobileApp()) {
            notifyMobileApp({type: 'share.request', url, title});
            return;
        }

        if (!navigator.share) {
            // In-app browsers (LinkedIn, Instagram…) and Android WebViews have no share sheet.
            navigator.clipboard
                ?.writeText(url)
                .then(() => {
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                })
                // Refused by the browser: nothing worth reporting.
                .catch(() => {});
            return;
        }

        navigator.share({title, url}).catch((e: unknown) => {
            // Dismissed by the visitor, tapped twice, or refused by the browser for want
            // of a user gesture: all theirs, none worth reporting.
            const name = (e as {name?: string} | null)?.name ?? String(e);
            if (!/AbortError|NotAllowedError|InvalidStateError/.test(name)) {
                throw e;
            }
        });
    };

    return (
        <>
            <Container
                maxWidth="sm"
                sx={{
                    padding: 0,
                }}
            >
                <Box
                    sx={theme => ({
                        padding: 0,
                        paddingBottom: theme.spacing(4),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(4),
                    })}
                >
                    <Card>
                        <CardMedia
                            sx={{
                                'backgroundColor': '#FFF',
                                '& .MuiButton-root': {
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease-in-out',
                                },
                                '&:hover .MuiButton-root': {
                                    opacity: 1,
                                },
                            }}
                            style={{position: 'relative'}}
                        >
                            <LogoWrapper
                                svgSpec={cover?.options}
                                rounded={!cover?.url}
                            >
                                <LoadingImg
                                    loadingMinHeight={`min(${coverHeight}px, ${(coverHeight / coverWidth) * 100}vw)`}
                                    src={cover?.url || defaultCover}
                                    alt={fullName}
                                />
                            </LogoWrapper>
                            {kompany?.siteUrl ? (
                                <Button
                                    color={'secondary'}
                                    variant={'contained'}
                                    sx={theme => ({
                                        position: 'absolute',
                                        textTransform: 'none',
                                        bottom: theme.spacing(1),
                                        right: theme.spacing(1),
                                    })}
                                    href={
                                        kompany!.trackingSiteUrl && !isPreview
                                            ? addSourceTracker(
                                                  kompany.trackingSiteUrl,
                                                  TrackingSource.Profile,
                                              )
                                            : kompany!.siteUrl
                                    }
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                    startIcon={<icons.linkExternal />}
                                >
                                    {humanizeUrl(kompany!.siteUrl)}
                                </Button>
                            ) : null}
                        </CardMedia>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                alignContent: 'center',
                                p: 2,
                            }}
                        >
                            {profile.avatar && (
                                <Avatar
                                    sx={(theme: Theme) => ({
                                        width: sizes,
                                        height: sizes,
                                        marginTop: {
                                            xs: `-25px`,
                                            md: `-30px`,
                                            lg: `-30px`,
                                        },
                                        marginBottom: theme.spacing(3),
                                        border: `1px solid #FFF`,
                                        boxShadow: theme.shadows[12],
                                    })}
                                >
                                    <LoadingImg
                                        src={profile.avatar!.url}
                                        alt={fullName}
                                    />
                                </Avatar>
                            )}
                            <div
                                style={{
                                    flexGrow: 1,
                                    textAlign: 'center',
                                }}
                            >
                                {fullName ? (
                                    <Typography
                                        gutterBottom
                                        variant="h1"
                                        component="div"
                                    >
                                        {fullName}
                                    </Typography>
                                ) : null}
                                {title ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: 18,
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                ) : null}
                                {kompany && !hideCompanyName ? (
                                    <Typography
                                        variant="body1"
                                        color="text.primary"
                                        sx={{
                                            mt: 1,
                                        }}
                                    >
                                        {kompany!.siteUrl ? (
                                            <a
                                                style={{
                                                    color: 'inherit',
                                                    textDecoration: 'inherit',
                                                }}
                                                href={kompany!.siteUrl}
                                                target={'_blank'}
                                                rel="noopener noreferrer"
                                            >
                                                {kompanyName!}
                                            </a>
                                        ) : (
                                            kompanyName!
                                        )}
                                    </Typography>
                                ) : null}
                            </div>
                        </Box>

                        <CardActions
                            sx={{
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            {(isPreview || vcfUrl) && (
                                <Button
                                    href={vcfUrl ?? '#'}
                                    size={'large'}
                                    variant={'contained'}
                                    color={'primary'}
                                    startIcon={<icons.vcard />}
                                >
                                    {!isIOS
                                        ? t(
                                              'app:profile.save_contact',
                                              'Contact info',
                                          )
                                        : t(
                                              'app:profile.save_contact_ios',
                                              'Contact info',
                                          )}
                                </Button>
                            )}{' '}
                            {options?.exchanges && (
                                <Button
                                    onClick={showExchangeModal}
                                    data-testid={'exchange'}
                                    size={'large'}
                                    variant={'contained'}
                                    color={'secondary'}
                                    startIcon={<icons.exchange />}
                                >
                                    {t('app:profile.exchange', 'Exchange')}
                                </Button>
                            )}
                        </CardActions>

                        <CardActions
                            sx={{
                                justifyContent: 'center',
                            }}
                        >
                            <Button
                                onClick={share}
                                size={'small'}
                                startIcon={<icons.shareProfile />}
                            >
                                {linkCopied
                                    ? t('app:profile.link_copied', 'Link copied')
                                    : t('app:profile.share', 'Share')}
                            </Button>
                        </CardActions>

                        {bio && <Bio bio={bio} />}

                        <div>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    flexWrap: 'wrap',
                                    mt: 2,
                                }}
                            >
                                {socials.map((i, index) => (
                                    <SocialCard
                                        key={index}
                                        {...i}
                                        isPreview={isPreview}
                                        userLocales={userLocales}
                                    />
                                ))}
                            </Box>
                            <Links
                                links={links}
                                locale={locale}
                                isPreview={isPreview}
                                userLocales={userLocales}
                            />
                        </div>
                        {blocks.length > 0 ? (
                            <RichBlocks
                                isPreview={isPreview}
                                blocks={blocks}
                                locale={profile.locale}
                                userLocales={userLocales}
                            />
                        ) : null}
                    </Card>
                    {!isPreview && !profile.options.noBranding ? (
                        <PoweredBy profileId={profile.id} />
                    ) : null}
                </Box>
            </Container>
        </>
    );
}

export default memo(
    ProfileViewContent,
    (a, b) => a.profile === b.profile && a.userLocales === b.userLocales,
);
